import React, { FC, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { PermissionWrapper } from 'src/features/permission-wrapper';
import { UserRole } from 'src/shared/api';
import {
  OrdersReprocessData,
  reprocessPendingReviewRequest,
} from 'src/entities/order';

type ReprocessError = 'access' | 'server';

export const ReprocessPendingReviewButton: FC = () => {
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<OrdersReprocessData | null>(null);
  const [errorKind, setErrorKind] = useState<ReprocessError | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const closeDialog = () => {
    setOpen(false);
    setRunning(false);
    setResult(null);
    setErrorKind(null);
    setErrorMessage(null);
  };

  const handleConfirm = async () => {
    setRunning(true);
    setErrorKind(null);
    setErrorMessage(null);

    try {
      const response = await reprocessPendingReviewRequest();
      setResult(response.data);
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        setErrorKind('access');
      } else {
        setErrorKind('server');
        const rawErrors = error?.response?.data?.errors;
        const messages = Array.isArray(rawErrors)
          ? rawErrors
              .map((item: any) =>
                typeof item === 'string' ? item : item?.message,
              )
              .filter(Boolean)
          : [];
        setErrorMessage(messages.length ? messages.join('; ') : null);
      }
    } finally {
      setRunning(false);
    }
  };

  const renderDialogContent = () => {
    if (running) {
      return (
        <Box display="flex" alignItems="center" gap={2}>
          <CircularProgress size={24} />
          <Typography>
            Выполняется повторная проводка. Пожалуйста, не закрывайте окно.
          </Typography>
        </Box>
      );
    }

    if (errorKind === 'access') {
      return (
        <Alert severity="error">
          Нет доступа: массовая повторная проводка доступна только
          администратору.
        </Alert>
      );
    }

    if (errorKind === 'server') {
      return (
        <Alert severity="error">
          Не удалось выполнить повторную проводку.
          {errorMessage ? ` ${errorMessage}` : ' Попробуйте ещё раз.'}
        </Alert>
      );
    }

    if (result) {
      return (
        <Box>
          <Alert
            severity={result.skipped.length ? 'warning' : 'success'}
            sx={{ mb: result.skipped.length ? 2 : 0 }}
          >
            Отправлено в обработку: {result.reprocessCount}
            {result.skipped.length ? `, пропущено: ${result.skipped.length}` : ''}
          </Alert>
          {result.skipped.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Пропущенные заказы:
              </Typography>
              <List dense disablePadding>
                {result.skipped.map(item => (
                  <ListItem key={item.orderId} disableGutters>
                    <ListItemText
                      primary={`Заказ #${item.orderId}`}
                      secondary={item.reason}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Box>
      );
    }

    return (
      <Typography>
        Будут повторно проведены заказы в состоянии PendingReview за последние
        24 часа. Продолжить?
      </Typography>
    );
  };

  const finished = Boolean(result || errorKind);

  return (
    <PermissionWrapper roles={[UserRole.ROLE_ADMIN]}>
      <Button variant="outlined" onClick={() => setOpen(true)}>
        Провести зависшие (24 ч)
      </Button>
      <Dialog
        open={open}
        onClose={running ? undefined : closeDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Массовая повторная проводка заказов</DialogTitle>
        <DialogContent dividers>{renderDialogContent()}</DialogContent>
        <DialogActions>
          {!running && !finished && (
            <Button
              onClick={handleConfirm}
              variant="contained"
              color="primary"
            >
              Провести
            </Button>
          )}
          <Button
            onClick={closeDialog}
            disabled={running}
            variant={finished ? 'contained' : 'outlined'}
          >
            {finished ? 'Закрыть' : 'Отмена'}
          </Button>
        </DialogActions>
      </Dialog>
    </PermissionWrapper>
  );
};
